import React, { useEffect, useState } from 'react';
import {
  Button,
  Table,
  Typography,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  notification,
} from 'antd';
import {
  KeyOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ScrollBar } from '~/Components/ScrollBar';
import { WaitOverlay } from '~/Components';
import { useAppDispatch } from '~/Store/hooks';
import { WaitOverlayActions } from '~/Store/Overlays';
import { errorNotification, PasskeyService, PasskeySummaryDto } from '~/Services';
import { base64UrlDecode, base64UrlEncode } from '~/Utils/base64url';
import styles from './Passkeys.module.less';

const { Title, Text } = Typography;
const waitOverlayName = 'fetchingPasskeys';

export const Passkeys: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [passkeys, setPasskeys] = useState<PasskeySummaryDto[]>([]);
  const [isWebAuthnSupported, setIsWebAuthnSupported] = useState(true);

  // Modal states
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [selectedPasskey, setSelectedPasskey] = useState<PasskeySummaryDto | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [addForm] = Form.useForm();
  const [renameForm] = Form.useForm();

  useEffect(() => {
    const supported = typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';
    setIsWebAuthnSupported(supported);
    fetchPasskeys();
  }, []);

  const fetchPasskeys = async () => {
    try {
      dispatch(WaitOverlayActions.setVisible({ name: waitOverlayName, value: true }));
      const res = await PasskeyService.Get.list();
      setPasskeys(res.data || []);
    } catch (err) {
      errorNotification(err);
    } finally {
      dispatch(WaitOverlayActions.setVisible({ name: waitOverlayName, value: false }));
    }
  };

  // --- FE-2.4: Register new passkey ---
  const handleOpenAddModal = () => {
    addForm.resetFields();
    setIsAddModalVisible(true);
  };

  const handleRegisterPasskey = async () => {
    try {
      const values = await addForm.validateFields();
      const deviceName = values.deviceName ? values.deviceName.trim() : undefined;
      setIsSubmitting(true);

      // 1. Get creation options from backend
      const res = await PasskeyService.Post.registerOptions();
      const { options, ceremonyToken } = res.data;

      // 2. Decode base64url binary fields for WebAuthn API
      const publicKey: PublicKeyCredentialCreationOptions = {
        ...options,
        challenge: base64UrlDecode(options.challenge),
        user: {
          ...options.user,
          id: base64UrlDecode(options.user.id),
        },
        excludeCredentials: (options.excludeCredentials || []).map((cred) => ({
          ...cred,
          type: cred.type as PublicKeyCredentialType,
          id: base64UrlDecode(cred.id),
        })),
        pubKeyCredParams: (options.pubKeyCredParams || []).map((param) => ({
          ...param,
          type: param.type as PublicKeyCredentialType,
        })),
      };

      // 3. Call browser WebAuthn API
      let credential: PublicKeyCredential | null = null;
      try {
        credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential;
      } catch (domErr: any) {
        // FE-2.7: Handle browser DOMExceptions with friendly messages
        if (domErr.name === 'NotAllowedError') {
          notification.warning({
            message: t('profile.passkeyCancelled'),
          });
        } else if (domErr.name === 'InvalidStateError') {
          notification.error({
            message: t('profile.passkeyAlreadyExists'),
          });
        } else {
          notification.error({
            message: domErr.message || t('profile.passkeyError'),
          });
        }
        return;
      }

      if (!credential) {
        notification.error({ message: t('profile.passkeyError') });
        return;
      }

      // 4. Encode assertion response to base64url
      const rawResponse = credential.response as AuthenticatorAttestationResponse;
      const attestationResponse = {
        id: credential.id,
        rawId: base64UrlEncode(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: base64UrlEncode(rawResponse.attestationObject),
          clientDataJson: base64UrlEncode(rawResponse.clientDataJSON),
        },
        clientExtensionResults: credential.getClientExtensionResults
          ? credential.getClientExtensionResults()
          : {},
      };

      // 5. Send to backend for verification
      await PasskeyService.Post.registerVerify({
        ceremonyToken,
        attestationResponse,
        deviceName,
      });

      notification.success({
        message: t('profile.passkeyRegisteredSuccess'),
      });

      setIsAddModalVisible(false);
      addForm.resetFields();
      fetchPasskeys();
    } catch (err: any) {
      if (err.errorFields) return; // Form validation error
      errorNotification(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- FE-2.5: Rename passkey ---
  const handleOpenRenameModal = (item: PasskeySummaryDto) => {
    setSelectedPasskey(item);
    renameForm.setFieldsValue({ label: item.label });
    setIsRenameModalVisible(true);
  };

  const handleRenameSubmit = async () => {
    if (!selectedPasskey) return;
    try {
      const values = await renameForm.validateFields();
      setIsSubmitting(true);
      await PasskeyService.Patch.rename(selectedPasskey.id, {
        label: values.label.trim(),
      });

      notification.success({
        message: t('profile.passkeyRenamedSuccess'),
      });

      setIsRenameModalVisible(false);
      setSelectedPasskey(null);
      fetchPasskeys();
    } catch (err: any) {
      if (err.errorFields) return;
      errorNotification(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- FE-2.6: Revoke passkey ---
  const handleRevoke = (item: PasskeySummaryDto) => {
    Modal.confirm({
      title: `${t('profile.revokePasskey')}: ${item.label}`,
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: t('profile.confirmRevokePasskey'),
      okText: t('profile.revokePasskey'),
      okType: 'danger',
      cancelText: t('cancel') || 'Cancel',
      onOk: async () => {
        try {
          await PasskeyService.Delete.revoke(item.id);
          notification.success({
            message: t('profile.passkeyRevokedSuccess'),
          });
          fetchPasskeys();
        } catch (err) {
          errorNotification(err);
        }
      },
    });
  };

  const columns = [
    {
      title: t('profile.deviceName'),
      dataIndex: 'label',
      key: 'label',
      render: (text: string) => (
        <div className={styles.DeviceCell}>
          <KeyOutlined className={styles.DeviceIcon} />
          <Text strong>{text || 'Passkey'}</Text>
        </div>
      ),
    },
    {
      title: t('profile.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
    },
    {
      title: t('profile.lastUsedAt'),
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (val: string | null) => (val ? new Date(val).toLocaleString() : t('profile.neverUsed')),
    },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_: any, record: PasskeySummaryDto) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenRenameModal(record)}
            className={styles.ActionBtn}
          >
            {t('profile.renamePasskey')}
          </Button>
          <Button
            type="link"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleRevoke(record)}
            className={styles.ActionBtn}
          >
            {t('profile.revokePasskey')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <WaitOverlay name={waitOverlayName} size="large">
      <div className={styles.PasskeysContainer}>
        <div className={styles.HeaderRow}>
          <Title level={4} style={{ margin: 0 }}>
            {t('profile.passkeys')}
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenAddModal}
            disabled={!isWebAuthnSupported}
          >
            {t('profile.addPasskey')}
          </Button>
        </div>

        {!isWebAuthnSupported && (
          <Alert
            message={t('profile.browserNotSupportPasskey')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Text className={styles.DescriptionText}>
          {t('profile.passkeyDescription')}
        </Text>

        <div style={{ flex: 1, minHeight: 0 }}>
          <ScrollBar style={{ paddingRight: '16px', marginRight: '-16px' }}>
            <div className={styles.TableCard}>
              <Table
                dataSource={passkeys}
                columns={columns}
                rowKey="id"
                pagination={false}
                locale={{ emptyText: t('profile.noPasskeys') }}
              />
            </div>
          </ScrollBar>
        </div>
      </div>

      {/* Add Passkey Modal */}
      <Modal
        title={t('profile.addPasskey')}
        visible={isAddModalVisible}
        onOk={handleRegisterPasskey}
        onCancel={() => setIsAddModalVisible(false)}
        confirmLoading={isSubmitting}
        okText={t('profile.addPasskey')}
        cancelText={t('cancel') || 'Cancel'}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="deviceName"
            label={t('profile.deviceName')}
            rules={[
              { max: 100, message: t('profile.deviceNameTooLong') },
            ]}
          >
            <Input
              placeholder={t('profile.enterDeviceName')}
              maxLength={100}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Rename Passkey Modal */}
      <Modal
        title={t('profile.renamePasskey')}
        visible={isRenameModalVisible}
        onOk={handleRenameSubmit}
        onCancel={() => {
          setIsRenameModalVisible(false);
          setSelectedPasskey(null);
        }}
        confirmLoading={isSubmitting}
        okText={t('save') || 'Save'}
        cancelText={t('cancel') || 'Cancel'}
        destroyOnClose
      >
        <Form form={renameForm} layout="vertical">
          <Form.Item
            name="label"
            label={t('profile.deviceName')}
            rules={[
              { required: true, message: t('profile.deviceNameRequired') },
              { max: 100, message: t('profile.deviceNameTooLong') },
            ]}
          >
            <Input
              placeholder={t('profile.enterDeviceName')}
              maxLength={100}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </WaitOverlay>
  );
};
